import { asyncWithLDProvider, useLDClient } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useState } from 'react'
import { useSourcePluginContext } from './SourcePluginContext'

// Re-identifies the LD context in place whenever the member changes, so flags
// track the current member without recreating the provider (which would remount
// the whole tree and leak the previous LD client).
const LDIdentifier: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const ldClient = useLDClient()
  const { pluginContext } = useSourcePluginContext()
  const member = pluginContext?.member

  useEffect(() => {
    if (ldClient && member) {
      ldClient.identify({ key: member, kind: 'careOps-user' })
    }
  }, [ldClient, member])

  return <>{children}</>
}

export const LaunchDarklyProvider: React.FC<{
  children: React.ReactNode
  clientId: string
}> = ({ children, clientId }) => {
  const [LDProvider, setLDProvider] = useState<React.ComponentType<{
    children: React.ReactNode
  }> | null>(null)

  // Create the provider exactly once (anonymous), independent of the member.
  // The member is applied later via identify to keep the tree stable and avoid
  // remounting descendants on every load.
  useEffect(() => {
    let cancelled = false

    asyncWithLDProvider({
      clientSideID: clientId,
      context: { kind: 'careOps-user', anonymous: true },
    }).then((Provider) => {
      if (!cancelled) {
        setLDProvider(() => Provider)
      }
    })

    return () => {
      cancelled = true
    }
  }, [clientId])

  if (!LDProvider) {
    return <>{children}</>
  }

  return (
    <LDProvider>
      <LDIdentifier>{children}</LDIdentifier>
    </LDProvider>
  )
}
